import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiPhone, FiMail, FiClock } from 'react-icons/fi';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    // Check if we're on the home page
    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isArabic = i18n.language ? i18n.language.startsWith('ar') : false;

    // Handle language change and direction
    const toggleLanguage = () => {
        const newLang = isArabic ? 'en' : 'ar';
        i18n.changeLanguage(newLang);
        document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = newLang;
    };

    // Ensure direction is correct on initial load and language changes
    useEffect(() => {
        document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
        document.documentElement.lang = isArabic ? 'ar' : 'en';
    }, [i18n.language, isArabic]);

    // Reordered per report recommendation: Services before Blogs, Careers near Contact
    const navLinks = [
        { name: t('nav.home'), path: '/' },
        { name: t('nav.about'), path: '/about' },
        { name: t('nav.services'), path: '/services' },
        { name: t('nav.blogs'), path: '/blogs' },
        { name: t('nav.careers'), path: '/careers' },
        { name: t('nav.contact'), path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    // Language Switcher Component
    const LanguageSwitcher = ({ mobile = false }) => (
        <button
            onClick={toggleLanguage}
            type="button"
            className={`font-semibold transition-colors text-sm px-3 py-1 rounded-lg border ${mobile
                ? 'block w-full text-start py-3 px-4 text-gray-700 hover:bg-gray-50 border-gray-200 mt-2'
                : scrolled || !isHomePage
                    ? 'text-gray-700 hover:text-primary-600 border-gray-200 hover:border-primary-300'
                    : 'text-white hover:text-accent-400 border-white/20 hover:border-accent-400/50'
                }`}
        >
            {isArabic ? 'English' : 'العربية'}
        </button>
    );

    // For Home page - transparent navbar with centered logo
    if (isHomePage) {
        const leftLinks = navLinks.slice(0, 3);
        const rightLinks = navLinks.slice(3);

        return (
            <>
                <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled
                    ? 'bg-white shadow-lg py-2.5'
                    : 'bg-transparent py-4'
                    }`}>
                    <div className="container-custom">
                        {scrolled ? (
                            /* Standard Sticky Layout when scrolled: Logo Left, All Links Right */
                            <div className="flex items-center justify-between">
                                {/* Logo */}
                                <Link to="/" className="flex items-center">
                                    <img
                                        src="/images/logo1.png"
                                        alt="PrimeForce Services W.L.L."
                                        className="h-12 md:h-14 w-auto object-contain transition-all duration-300"
                                    />
                                </Link>

                                {/* Desktop Navigation (All Links) */}
                                <div className="hidden lg:flex items-center gap-6 xl:gap-8">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={`text-sm font-semibold tracking-wide transition-all duration-300 relative group ${isActive(link.path)
                                                ? 'text-primary-600'
                                                : 'text-gray-700 hover:text-primary-600'
                                                }`}
                                        >
                                            {link.name}
                                            <span className={`absolute -bottom-1 start-0 h-0.5 bg-primary-600 transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                                        </Link>
                                    ))}
                                    <LanguageSwitcher />
                                </div>

                                {/* Mobile Menu Button */}
                                <button
                                    className="lg:hidden p-2 text-gray-700 hover:text-primary-600 transition-colors"
                                    onClick={() => setIsOpen(!isOpen)}
                                    aria-label="Toggle menu"
                                >
                                    {isOpen ? <FiX size={28} /> : <FiMenu size={28} />}
                                </button>
                            </div>
                        ) : (
                            /* Centered Logo Layout when at top */
                            <div className="flex items-center justify-between">
                                {/* Left Nav Links - Desktop */}
                                <div className="hidden lg:flex items-center gap-8 flex-1 justify-start">
                                    {leftLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={`text-sm font-semibold tracking-wide transition-all duration-300 relative group ${isActive(link.path)
                                                ? 'text-accent-400'
                                                : 'text-white hover:text-accent-400'
                                                }`}
                                        >
                                            {link.name}
                                            <span className={`absolute -bottom-1 start-0 h-0.5 bg-accent-500 transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                                        </Link>
                                    ))}
                                </div>

                                {/* Centered Logo */}
                                <Link to="/" className="flex items-center justify-center">
                                    <img
                                        src="/images/logo1.png"
                                        alt="PrimeForce Services W.L.L."
                                        className="h-20 md:h-24 w-auto object-contain transition-all duration-500"
                                    />
                                </Link>

                                {/* Right Nav Links - Desktop */}
                                <div className="hidden lg:flex items-center gap-8 flex-1 justify-end">
                                    {rightLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={`text-sm font-semibold tracking-wide transition-all duration-300 relative group ${isActive(link.path)
                                                ? 'text-accent-400'
                                                : 'text-white hover:text-accent-400'
                                                }`}
                                        >
                                            {link.name}
                                            <span className={`absolute -bottom-1 start-0 h-0.5 bg-accent-500 transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                                        </Link>
                                    ))}
                                    <LanguageSwitcher />
                                </div>

                                {/* Mobile Menu Button */}
                                <button
                                    className="lg:hidden p-2 text-white hover:text-accent-400 transition-colors"
                                    onClick={() => setIsOpen(!isOpen)}
                                    aria-label="Toggle menu"
                                >
                                    {isOpen ? <FiX size={28} /> : <FiMenu size={28} />}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu */}
                    <AnimatePresence>
                        {isOpen && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="lg:hidden bg-white border-t border-gray-100 overflow-hidden shadow-lg"
                            >
                                <div className="container-custom py-4 space-y-3">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={() => setIsOpen(false)}
                                            className={`block py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${isActive(link.path)
                                                ? 'bg-primary-50 text-primary-600'
                                                : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                    <LanguageSwitcher mobile={true} />

                                    {/* Mobile Contact Info */}
                                    <div className="pt-4 border-t border-gray-100 space-y-3">
                                        <a href="tel:+97317701616" className="flex items-center gap-3 text-gray-600 hover:text-primary-600">
                                            <FiPhone className="text-primary-600" />
                                            <span>+973 1770 1616</span>
                                        </a>
                                        <a href="mailto:info@primeforceservices.com" className="flex items-center gap-3 text-gray-600 hover:text-primary-600">
                                            <FiMail className="text-primary-600" />
                                            <span>info@primeforceservices.com</span>
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </nav>
            </>
        );
    }

    // For Other Pages - White navbar with top info bar
    return (
        <>
            {/* Top Info Bar */}
            <div className="bg-primary-600 text-white py-2.5 hidden md:block border-b border-primary-700">
                <div className="container-custom flex justify-between items-center text-sm">
                    <div className="flex items-center gap-8">
                        <a href="tel:+97317701616" className="flex items-center gap-2 hover:text-accent-300 transition-colors">
                            <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                                <FiPhone className="text-white" size={14} />
                            </div>
                            <div>
                                <span className="text-white/70 text-xs block">{t('nav.contactInfo')}</span>
                                <span className="font-semibold">+973 1770 1616</span>
                            </div>
                        </a>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                                <FiClock className="text-white" size={14} />
                            </div>
                            <div>
                                <span className="text-white/70 text-xs block">{t('nav.workingHours')}</span>
                                <span className="font-semibold">{t('nav.workingHoursValue')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <LanguageSwitcher />
                        <Link to="/contact" className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-colors shadow-md">
                            {t('nav.getQuote')}
                        </Link>
                    </div>
                </div>
            </div>

            {/* Main Navigation */}
            <nav className={`fixed top-0 inset-x-0 z-50 bg-white transition-all duration-300 ${scrolled ? 'shadow-lg py-2' : 'shadow-sm py-3'}`}>
                <div className="container-custom">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <Link to="/" className="flex items-center">
                            <img
                                src="/images/logo1.png"
                                alt="PrimeForce Services W.L.L."
                                className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-12 md:h-14' : 'h-16 md:h-20'}`}
                            />
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center gap-6">
                            <div className="flex items-center gap-1 relative">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`relative px-5 py-4 text-sm font-semibold tracking-wide transition-all duration-300 ${isActive(link.path)
                                            ? 'text-primary-600'
                                            : 'text-gray-700 hover:text-primary-600'
                                            }`}
                                    >
                                        {isActive(link.path) && (
                                            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary-600 rounded-b-full"></span>
                                        )}
                                        {link.name}
                                    </Link>
                                ))}
                            </div>
                            <LanguageSwitcher />
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            className="lg:hidden p-2 text-gray-700 hover:text-primary-600 transition-colors"
                            onClick={() => setIsOpen(!isOpen)}
                            aria-label="Toggle menu"
                        >
                            {isOpen ? <FiX size={28} /> : <FiMenu size={28} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
                        >
                            <div className="container-custom py-4 space-y-3">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        onClick={() => setIsOpen(false)}
                                        className={`block py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${isActive(link.path)
                                            ? 'bg-primary-50 text-primary-600'
                                            : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                                <LanguageSwitcher mobile={true} />
                                <Link
                                    to="/contact"
                                    onClick={() => setIsOpen(false)}
                                    className="block btn-accent text-center mt-4"
                                >
                                    {t('nav.getQuote')}
                                </Link>

                                {/* Mobile Contact Info */}
                                <div className="pt-4 border-t border-gray-100 space-y-3">
                                    <a href="tel:+97317701616" className="flex items-center gap-3 text-gray-600 hover:text-primary-600">
                                        <FiPhone className="text-primary-600" />
                                        <span>+973 1770 1616</span>
                                    </a>
                                    <a href="mailto:info@primeforceservices.com" className="flex items-center gap-3 text-gray-600 hover:text-primary-600">
                                        <FiMail className="text-primary-600" />
                                        <span>info@primeforceservices.com</span>
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>
        </>
    );
};

export default Navbar;
